import React from "react";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { NewsletterSignup } from "../components/NewsletterSignup";
import { Heart, Shield, Sparkles, Star, PawPrint } from "lucide-react";
import { motion } from "framer-motion";

// Full story text — provided by Chris, used verbatim per his request.
const STORY_PARAGRAPHS = [
    "I created Hands of Hope in 2025 because I wanted to help people and animals who were struggling, hurting, or forgotten. That mission grew into Real-Check.org — a place where anyone can come to breathe, reset, and find clarity when life feels overwhelming.",
    "I'm a disabled veteran, and I've lived through enough hard moments to know how important it is to have somewhere safe to turn. Hands of Hope taught me how much people need truth, grounding, and compassion — and how many animals need the same. Real-Check.org is my way of making that support available to anyone, anywhere, at any time.",
    "This project is dedicated to my mama, whose strength shaped my heart, and to Charlie, who keeps me grounded every single day. They're part of the reason I show up, part of the reason I built this, and part of the reason I believe no person or animal should ever be left behind.",
    "Real-Check.org is here to help you breathe, find your footing, and get honest support when you need it most. That's the promise I made when I started Hands of Hope — and it's the promise I'm keeping here.",
];

const CHARLIE_LINES = [
    "Charlie is why this exists.",
    "Every time the world got heavy — and it got heavy — he stayed.",
    "Quiet company through the hardest nights.",
    "He's the reason I got up on days I didn't want to.",
    "The reason I remembered gentleness still matters.",
    "Every animal deserves what he's given me:",
    "someone to notice them, someone to stay.",
    "Charlie is the hero of this whole story.",
];

const MAMA_LINES = [
    "She's why I know what strength looks like.",
    "Not the loud kind — the quiet kind.",
    "The kind that keeps going after every reason to stop.",
    "She taught me that being kind isn't being weak.",
    "That helping people costs less than looking away.",
    "That the ones with the least often give the most.",
    "Everything Hands of Hope stands for came from her.",
    "This site, this mission, this promise — it's all her.",
];

const CHARLIE_IMG = "/charlie.webp";
const MAMA_IMG = "https://customer-assets.emergentagent.com/job_fact-mission/artifacts/nx7gnhsu_%28Upload-from-mobile-1767436142%292020-09-27_Original.jpeg";

export default function About() {
    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <p className="text-xs uppercase tracking-widest text-primary mb-3">The story</p>
                <h1 className="font-display text-4xl md:text-6xl text-accent tracking-tight leading-[1.05]">
                    Built to help you breathe,<br />
                    <span className="text-primary">find your footing</span>, and get honest support.
                </h1>
            </motion.div>

            <div className="mt-16 space-y-7" data-testid="about-story">
                {STORY_PARAGRAPHS.map((p, i) => (
                    <motion.p
                        key={p.slice(0, 40)}
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.08 }}
                        className="text-lg md:text-xl text-accent/90 leading-relaxed"
                        data-testid={`about-paragraph-${i}`}
                    >
                        {p}
                    </motion.p>
                ))}
            </div>

            {/* CHARLIE — the hero */}
            <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mt-20 relative"
                data-testid="about-charlie-section"
            >
                <div className="grid md:grid-cols-5 gap-8 items-center">
                    <div className="md:col-span-2 relative">
                        <div className="absolute -top-3 -left-3 z-10 flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest px-3 py-1 shadow-lg">
                            <Star className="h-3 w-3" fill="currentColor" /> The Hero
                        </div>
                        <div className="relative aspect-[3/4] rounded-3xl overflow-hidden border border-white/10 badge-glow-primary">
                            <img
                                src={CHARLIE_IMG}
                                alt="Charlie — the hero of Real-Check"
                                className="w-full h-full object-cover"
                                data-testid="about-charlie-image"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent pointer-events-none" />
                        </div>
                    </div>
                    <div className="md:col-span-3">
                        <div className="flex items-center gap-2 mb-3">
                            <PawPrint className="h-4 w-4 text-primary" />
                            <p className="text-xs uppercase tracking-widest text-primary">For Charlie</p>
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl text-accent tracking-tight leading-tight mb-6">
                            The hero of this whole story.
                        </h2>
                        <div className="space-y-2" data-testid="about-charlie-dedication">
                            {CHARLIE_LINES.map((line, i) => (
                                <motion.p
                                    key={i}
                                    initial={{ opacity: 0, x: -8 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.05 }}
                                    className="text-lg text-accent/90 leading-relaxed"
                                >
                                    {line}
                                </motion.p>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* MAMA */}
            <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mt-24 relative"
                data-testid="about-mama-section"
            >
                <div className="grid md:grid-cols-5 gap-8 items-center">
                    <div className="md:col-span-3 md:order-1 order-2">
                        <div className="flex items-center gap-2 mb-3">
                            <Heart className="h-4 w-4 text-primary" />
                            <p className="text-xs uppercase tracking-widest text-primary">For Mama</p>
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl text-accent tracking-tight leading-tight mb-6">
                            The strength that shaped my heart.
                        </h2>
                        <div className="space-y-2" data-testid="about-mama-dedication">
                            {MAMA_LINES.map((line, i) => (
                                <motion.p
                                    key={i}
                                    initial={{ opacity: 0, x: -8 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.05 }}
                                    className="text-lg text-accent/90 leading-relaxed"
                                >
                                    {line}
                                </motion.p>
                            ))}
                        </div>
                    </div>
                    <div className="md:col-span-2 md:order-2 order-1 relative">
                        <div className="absolute -top-3 -right-3 z-10 flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground text-[10px] font-bold uppercase tracking-widest px-3 py-1 shadow-lg">
                            <Heart className="h-3 w-3" fill="currentColor" /> Family
                        </div>
                        <div className="relative aspect-square rounded-3xl overflow-hidden border border-white/10">
                            <img
                                src={MAMA_IMG}
                                alt="Chris and his mama"
                                className="w-full h-full object-cover"
                                data-testid="about-mama-image"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent pointer-events-none" />
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* Dedication card */}
            <Card className="card-surface mt-24 p-8 rounded-2xl" data-testid="about-dedication">
                <div className="flex items-center gap-3 mb-3">
                    <Heart className="h-5 w-5 text-primary" />
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Dedicated to</p>
                </div>
                <p className="font-display text-2xl md:text-3xl text-accent leading-snug">
                    My mama, whose strength shaped my heart —<br />
                    and Charlie, who keeps me grounded every day.
                </p>
                <p className="text-sm text-muted-foreground mt-6 italic">
                    — Chris · Disabled Veteran · Founder of Hands of Hope (2025)
                </p>
            </Card>

            {/* Three-pillar cards */}
            <div className="grid md:grid-cols-3 gap-4 mt-14">
                {[
                    { icon: Heart, title: "Compassion", body: "For every person and every animal that's been struggling, hurting, or forgotten." },
                    { icon: Shield, title: "Truth", body: "Clarity when the world feels loud, chaotic, or dishonest. No spin. No agenda." },
                    { icon: Sparkles, title: "Grounding", body: "A safe place to breathe, reset, and find your footing — anytime, anywhere." },
                ].map((f, i) => (
                    <Card key={i} className="card-surface p-6 rounded-2xl">
                        <f.icon className="h-5 w-5 text-primary mb-3" />
                        <h3 className="font-display text-lg text-accent mb-1">{f.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                    </Card>
                ))}
            </div>

            {/* CTAs */}
            <div className="mt-14 flex flex-wrap gap-3 justify-center">
                <Link to="/donate">
                    <Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-6" data-testid="about-donate-cta">
                        <Heart className="h-4 w-4 mr-2" /> Support Hands of Hope
                    </Button>
                </Link>
                <Link to="/">
                    <Button variant="secondary" className="rounded-full h-12 px-6" data-testid="about-tryapp-cta">
                        Try the truth engine →
                    </Button>
                </Link>
            </div>

            <div className="mt-16">
                <NewsletterSignup source="about" />
            </div>
        </div>
    );
}
